import React, { useEffect, useState } from 'react';
import propTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import Button from 'tailwind-components/Button/Button';
import Modal from 'tailwind-components/Modal/Modal';
import TextField from 'tailwind-components/TextField/TextField';
import TextArea from 'tailwind-components/TextArea/TextArea';
import {
  createPortalNote,
  updatePortalNote,
  deletePortalNote
} from 'state/actions/actions';
import validateNote from './createNoteValidator';

const EMPTY_ERRORS = { title: '', description: '' };

// avoids a nested ternary in the footer
function getSubmitLabel(pendingAction, isNew) {
  if (pendingAction === 'save') return 'Saving...';
  return isNew ? 'Create' : 'Save';
}

/**
 * modal used to create, edit and delete the agent's portal notes
 * @param closeModal - closes the modal without saving
 * @param title - current title of the note being edited, empty when creating
 * @param description - current body of the note being edited, empty when creating
 * @param id - zoho id of the note being edited; absent when creating
 * @param date - creation date of the note, sent back on update
 * @param active - whether the modal is open
 */
export default function CreateNoteForm({
  closeModal,
  title,
  description,
  id,
  date,
  active
}) {
  const dispatch = useDispatch();
  const [formState, setFormState] = useState({
    title: '',
    description: '',
    errors: EMPTY_ERRORS
  });
  const [pendingAction, setPendingAction] = useState('');
  const [submitError, setSubmitError] = useState('');
  const isNew = !id;
  const isBusy = pendingAction !== '';
  const isUnchanged =
    title === formState.title && description === formState.description;

  useEffect(() => {
    // fill the inputs with the note that was clicked
    setFormState({ title, description, errors: EMPTY_ERRORS });
    setPendingAction('');
    setSubmitError('');
  }, [title, description]);

  function setField(name, value) {
    setFormState(prev => ({
      ...prev,
      [name]: value,
      errors: { ...prev.errors, [name]: '' }
    }));
  }

  // keeps the pending flag and the error message in sync for every note action
  async function runAction(action, thunk, failureMessage) {
    setPendingAction(action);
    setSubmitError('');
    const result = await dispatch(thunk);
    setPendingAction('');
    if (!result?.ok) setSubmitError(failureMessage);
  }

  function onSubmit() {
    const { errors, isValid } = validateNote(formState);
    if (!isValid) {
      setFormState(prev => ({ ...prev, errors }));
      return;
    }
    if (isNew) {
      runAction(
        'save',
        createPortalNote(closeModal, {
          title: formState.title,
          description: formState.description
        }),
        'Could not create the note. Please try again.'
      );
      return;
    }
    runAction(
      'save',
      updatePortalNote(closeModal, {
        title: formState.title,
        description: formState.description,
        id,
        date
      }),
      'Could not save the note. Please try again.'
    );
  }

  function deleteNote() {
    runAction(
      'delete',
      deletePortalNote(closeModal, id),
      'Could not delete the note. Please try again.'
    );
  }

  return (
    <Modal
      variant='elevated'
      active={active}
      closeModal={closeModal}
      size='small'
      title={isNew ? 'Create note' : 'Edit note'}
      footer={
        <div className='flex items-center justify-end gap-2'>
          {!isNew && (
            <Button
              text={pendingAction === 'delete' ? 'Deleting...' : 'Delete'}
              variant='danger'
              outlined
              size='small'
              rounded='medium'
              onClick={deleteNote}
              disabled={isBusy}
              className='mr-auto'
            />
          )}
          <Button
            text='Cancel'
            variant='transparent'
            size='small'
            rounded='medium'
            onClick={closeModal}
            disabled={isBusy}
          />
          <Button
            text={getSubmitLabel(pendingAction, isNew)}
            onClick={onSubmit}
            variant='primaryLight'
            size='small'
            rounded='medium'
            disabled={isBusy || isUnchanged}
          />
        </div>
      }
    >
      <form className='flex w-full flex-col gap-4'>
        {submitError && (
          <p
            role='alert'
            className='m-0 rounded-lg bg-semantic-danger/10 px-3 py-2 text-sm text-semantic-danger'
          >
            {submitError}
          </p>
        )}
        <TextField
          label='Note title'
          name='title'
          value={formState.title}
          onChange={value => setField('title', value)}
          error={formState.errors.title}
          maxLength={255}
          placeholder='Note title'
        />
        <TextArea
          label='Description'
          name='description'
          value={formState.description}
          onChange={value => setField('description', value)}
          error={formState.errors.description}
          rows={7}
          maxLength={2000}
          showCounter
          placeholder='Add a description'
        />
      </form>
    </Modal>
  );
}

CreateNoteForm.propTypes = {
  active: propTypes.bool,
  closeModal: propTypes.func,
  title: propTypes.string,
  description: propTypes.string,
  id: propTypes.string,
  date: propTypes.string
};
